import {createContext} from "preact";
import {Inputs, useContext, useEffect, useRef, useState} from "preact/hooks";
import {useLoader} from "./generic/loader";

export type FetchProps = {
    interval?: number;
    forceShowLoader?: boolean;
};

export const FetchContext = createContext<FetchProps>({});

export function useFetchAndRender<T, I extends Inputs = unknown[]>(
    refresh: (...inputs: I) => Promise<T>, props?: FetchProps, inputs: I = ([] as unknown as I)
): [T | undefined, (forceShowLoader?: boolean) => void] {
    const updater = useRef<number | null>(null);
    const running = useRef<boolean>(false);
    const schedule = useRef<boolean>(true);

    const [result, setResult] = useState<T>();
    const ctx = useContext(FetchContext);
    const interval = props?.interval ?? ctx.interval;

    const resultRef = useRef(result);
    resultRef.current = result;

    const [showLoader, hideLoader] = useLoader();

    const requestRefresh = (forceShowLoader: boolean = false) => {
        if (updater.current !== null) {
            window.clearTimeout(updater.current);
            updater.current = null;
        }

        if (running.current) {
            return;
        }

        if (forceShowLoader) {
            showLoader();
        }

        running.current = true;
        refresh(...inputs)
            .then((res: T) => {
                setResult(res);
            })
            .finally(() => {
                if (interval && schedule.current) {
                    updater.current = window.setTimeout(() => {
                        updater.current = null;
                        requestRefresh();
                    }, interval * 1000);
                    //console.log(`Will reload after ${interval} seconds.`);
                }
                running.current = false;

                if (forceShowLoader) {
                    hideLoader();
                }
            });
    }

    useEffect(() => {
        requestRefresh(resultRef.current === undefined || (props?.forceShowLoader ?? false));

        return () => {
            if (updater.current) {
                window.clearTimeout(updater.current);
                updater.current = null;
            }

            schedule.current = false;
        }
    },  inputs);

    return [result, requestRefresh];
}
